import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth.jsx';

const ROLES = ['nurse','pharmacist','tech','admin','delivery'];

function randomTempPassword() {
  const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
  let out = '';
  for (let i = 0; i < 12; i++) out += chars[Math.floor(Math.random() * chars.length)];
  return out;
}

export default function AdminUsers() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [created, setCreated] = useState(null); // {email, temp_password} for show-once panel

  async function load() {
    setLoading(true);
    try { const r = await api.listUsers(); setUsers(r.users); setErr(null); }
    catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  if (user?.role !== 'admin') {
    return (
      <div className="card p-6">
        <div className="text-red-700 font-medium">Admin role required</div>
        <div className="text-sm text-slate-600">You don't have permission to view this page.</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Users</h1>
          <p className="text-sm text-slate-500">Add staff, assign roles, deactivate, or reset a password.</p>
        </div>
        <button className="btn-primary" onClick={() => setShowAdd(true)}>+ Add user</button>
      </div>

      {created && (
        <div className="card p-4 border-l-4 border-emerald-500 bg-emerald-50">
          <div className="font-medium text-emerald-900">User created</div>
          <div className="text-sm text-emerald-800 mt-1">
            Share these credentials securely with <strong>{created.email}</strong>. They'll be required to set a new password on first login.
          </div>
          <div className="mt-2 font-mono text-sm bg-white px-3 py-2 rounded border border-emerald-200">
            email: {created.email}<br/>
            temporary password: <span className="font-bold">{created.temp_password}</span>
          </div>
          <button className="text-xs text-emerald-700 mt-2 hover:underline" onClick={() => setCreated(null)}>
            Dismiss
          </button>
        </div>
      )}

      {showAdd && <AddUserForm onClose={() => setShowAdd(false)}
        onCreated={(c) => { setCreated(c); setShowAdd(false); load(); }} />}

      <div className="card p-0 overflow-hidden">
        {loading && <div className="p-6 text-slate-500">Loading…</div>}
        {err && <div className="p-6 text-red-600">{err}</div>}
        {!loading && !err && (
          <div className="table-wrap">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-4 py-2">Name</th>
                <th className="text-left px-4 py-2">Email</th>
                <th className="text-left px-4 py-2">Role</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-left px-4 py-2">Last login</th>
                <th className="text-left px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => <UserRow key={u.id} u={u} onChange={load} />)}
              {!users.length && <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-500">No users yet</td></tr>}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
}

function UserRow({ u, onChange }) {
  const [busy, setBusy] = useState(false);
  async function setRole(role) {
    setBusy(true);
    try { await api.patchUser(u.id, { role }); onChange(); }
    finally { setBusy(false); }
  }
  async function toggleActive() {
    setBusy(true);
    try { await api.patchUser(u.id, { active: !u.active }); onChange(); }
    finally { setBusy(false); }
  }
  async function reset() {
    const tp = randomTempPassword();
    if (!window.confirm(`Reset ${u.email}'s password to "${tp}"?\n\nUser will be forced to set a new password on next login.`)) return;
    setBusy(true);
    try { await api.resetUserPassword(u.id, { temp_password: tp });
          alert(`Password reset.\n\nTemporary password: ${tp}\n\nShare securely.`); }
    finally { setBusy(false); }
  }
  return (
    <tr className="border-t border-slate-100">
      <td className="px-4 py-2">
        {u.full_name}
        {u.must_change_password && <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">pending pw change</span>}
      </td>
      <td className="px-4 py-2 text-slate-600">{u.email}</td>
      <td className="px-4 py-2">
        <select className="input text-sm" value={u.role} disabled={busy}
                onChange={e => setRole(e.target.value)}>
          {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
      </td>
      <td className="px-4 py-2">
        <span className={`text-xs px-2 py-0.5 rounded-full ${u.active ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-700'}`}>
          {u.active ? 'active' : 'disabled'}
        </span>
      </td>
      <td className="px-4 py-2 text-slate-500 text-xs">
        {u.last_login_at ? new Date(u.last_login_at).toLocaleString() : '—'}
      </td>
      <td className="px-4 py-2 space-x-2">
        <button className="text-xs hover:underline" style={{ color: 'var(--brand-primary)' }} onClick={toggleActive} disabled={busy}>
          {u.active ? 'Disable' : 'Enable'}
        </button>
        <button className="text-xs text-slate-600 hover:underline" onClick={reset} disabled={busy}>
          Reset password
        </button>
      </td>
    </tr>
  );
}

function AddUserForm({ onClose, onCreated }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('nurse');
  const [tempPw, setTempPw] = useState(randomTempPassword());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  async function submit(e) {
    e.preventDefault();
    setErr(null); setBusy(true);
    try {
      await api.createUser({ email, full_name: name, role, temp_password: tempPw });
      onCreated({ email, temp_password: tempPw });
    } catch (e) {
      setErr(e.body?.message || e.message || 'failed');
    } finally { setBusy(false); }
  }

  return (
    <form onSubmit={submit} className="card p-4 space-y-3">
      <div className="font-medium">Add user</div>
      <div className="grid md:grid-cols-2 gap-3">
        <label className="block text-sm">
          <span className="text-xs text-slate-600">Full name</span>
          <input className="input" required value={name} onChange={e => setName(e.target.value)} />
        </label>
        <label className="block text-sm">
          <span className="text-xs text-slate-600">Email</span>
          <input className="input" type="email" required value={email} onChange={e => setEmail(e.target.value)} />
        </label>
        <label className="block text-sm">
          <span className="text-xs text-slate-600">Role</span>
          <select className="input" value={role} onChange={e => setRole(e.target.value)}>
            {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </label>
        <label className="block text-sm">
          <span className="text-xs text-slate-600">Temporary password (auto-generated, editable)</span>
          <div className="flex gap-2">
            <input className="input font-mono" value={tempPw} onChange={e => setTempPw(e.target.value)} />
            <button type="button" className="text-xs px-2 rounded border border-slate-300 hover:bg-slate-50"
                    onClick={() => setTempPw(randomTempPassword())}>↻</button>
          </div>
          <span className="text-xs text-slate-500">User must change on first login.</span>
        </label>
      </div>
      {err && <div className="text-sm text-red-600">{err}</div>}
      <div className="flex gap-2">
        <button className="btn-primary" disabled={busy}>{busy ? 'Creating…' : 'Create user'}</button>
        <button type="button" className="text-sm text-slate-600 hover:underline" onClick={onClose}>Cancel</button>
      </div>
    </form>
  );
}
